import { supabase } from '../lib/supabase';
import {
  DiscountApplication,
  DiscountWithApplication,
  AttachmentFile,
} from '../types/discount';
import { selectInChunks } from '../utils/chunkedIn';

// =====================================================
// 파일 업로드 (private 버킷)
// =====================================================

export async function uploadDiscountDocument(
  file: File,
  userId: string,
  discountId: string
): Promise<AttachmentFile> {
  const fileExt = file.name.split('.').pop()?.toLowerCase();
  const allowedExts = ['pdf', 'jpg', 'jpeg', 'png'];

  if (!fileExt || !allowedExts.includes(fileExt)) {
    throw new Error('PDF, JPG, PNG 파일만 업로드 가능합니다.');
  }

  if (file.size > 10 * 1024 * 1024) {
    throw new Error('파일 크기는 10MB 이하만 가능합니다.');
  }

  const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
  const filePath = `${userId}/${discountId}/${fileName}`;

  const { error } = await supabase.storage
    .from('discount-documents')
    .upload(filePath, file, {
      cacheControl: '31536000',
      upsert: false,
    });

  if (error) {
    throw new Error(`파일 업로드 실패: ${error.message}`);
  }

  return {
    name: file.name,
    url: filePath,
    size: file.size,
  };
}

// 관리자용: signed URL 생성
export async function getDocumentSignedUrl(filePath: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from('discount-documents')
    .createSignedUrl(filePath, 3600); // 1시간

  if (error || !data?.signedUrl) {
    throw new Error('파일 URL 생성 실패');
  }
  return data.signedUrl;
}

// =====================================================
// 수강생용 감면 서비스
// =====================================================

// 어떤 단계도 무한히 기다리지 않게 한다.
// 모바일에서 연결이 끊기면 fetch는 에러를 내지 않고 그대로 매달린다.
// 그러면 화면은 "신청 중..."에서 영원히 멈추고, 사용자는 뭐가 잘못됐는지 알 방법이 없다.
function withTimeout<T>(work: PromiseLike<T>, ms: number, onTimeout: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(onTimeout)), ms);
    work.then(
      value => { clearTimeout(timer); resolve(value); },
      error => { clearTimeout(timer); reject(error); }
    );
  });
}

function newDiscountId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // randomUUID가 없는 구형 브라우저용 대체
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export interface SubmitDiscountRequest {
  application_id: string;
  discount_reason_code: number;
  discount_reason_text: string;
  discount_type: '100%' | '50%';
  student_name: string;
  birthdate?: string;
  guardian_name?: string;
  address?: string;
  guardian_phone?: string;
  contact_phone?: string;
  bank_name?: string;
  account_number?: string;
  account_holder?: string;
  privacy_consent: boolean;
  user_confirmation: boolean;
  files: File[];
}

/**
 * 감면 신청 제출.
 *
 * 첨부서류를 먼저 올리고 신청 레코드를 마지막에 만든다. 순서가 중요하다.
 * 예전에는 레코드를 먼저 만들고 파일을 올렸는데, 업로드가 실패하면
 * 첨부서류 없는 신청만 남고 마이페이지에서는 버튼이 사라져 다시 올릴 수도 없었다.
 * (실제로 116건 중 5건이 그 상태다.)
 * 레코드 id를 클라이언트에서 미리 만들어 두면 업로드 경로를 먼저 정할 수 있다.
 *
 * @param userId 로그인한 사용자 id. 예전엔 여기서 auth.getUser()를 다시 불렀는데,
 *               이 호출은 네트워크와 인증 잠금을 함께 타서 멈출 수 있는 지점이었다.
 */
export async function submitDiscountApplication(
  request: SubmitDiscountRequest,
  userId: string,
  onProgress?: (message: string) => void
): Promise<{ success: boolean; data?: DiscountApplication; error?: string }> {
  try {
    if (!userId) {
      return { success: false, error: '로그인이 필요합니다.' };
    }

    if (!request.privacy_consent) {
      return { success: false, error: '개인정보 수집 이용에 동의해주세요.' };
    }
    if (!request.user_confirmation) {
      return { success: false, error: '이용자 확인사항에 동의해주세요.' };
    }

    const discountId = newDiscountId();

    // 1. 첨부서류 업로드 (실패하면 아무것도 안 남는다 → 그대로 다시 신청하면 된다)
    const attachments: AttachmentFile[] = [];
    for (let i = 0; i < request.files.length; i++) {
      onProgress?.(`서류 업로드 중 (${i + 1}/${request.files.length})`);
      const uploaded = await withTimeout(
        uploadDiscountDocument(request.files[i], userId, discountId),
        90_000,
        `"${request.files[i].name}" 업로드가 끝나지 않았습니다. 연결 상태를 확인하고 다시 시도해주세요.`
      );
      attachments.push(uploaded);
    }

    // 2. 신청 레코드 생성
    onProgress?.('신청서 접수 중');
    const { data: discount, error: insertError } = await withTimeout(
      supabase
      .from('discount_applications')
      .insert({
        id: discountId,
        application_id: request.application_id,
        user_id: userId,
        discount_type: request.discount_type,
        discount_reason_code: request.discount_reason_code,
        discount_reason_text: request.discount_reason_text,
        student_name: request.student_name,
        birthdate: request.birthdate ? request.birthdate.replace(/-/g, '').substring(0, 8) : null,
        guardian_name: request.guardian_name || null,
        address: request.address || null,
        guardian_phone: request.guardian_phone || null,
        contact_phone: request.contact_phone || null,
        bank_name: request.bank_name || null,
        account_number: request.account_number || null,
        account_holder: request.account_holder || null,
        privacy_consent: request.privacy_consent,
        user_confirmation: request.user_confirmation,
        attachments,
        status: 'submitted',
      })
      .select()
      .single(),
      20_000,
      '신청서 접수가 끝나지 않았습니다. 마이페이지를 새로고침해 접수 여부를 확인해주세요.'
    );

    if (insertError) {
      return { success: false, error: insertError.message };
    }

    return { success: true, data: discount };
  } catch (error: any) {
    return { success: false, error: error.message || '감면 신청 중 오류가 발생했습니다.' };
  }
}

// 특정 수강 신청에 대한 감면 신청 조회
export async function getDiscountByApplication(
  applicationId: string
): Promise<DiscountApplication | null> {
  const { data, error } = await supabase
    .from('discount_applications')
    .select('*')
    .eq('application_id', applicationId)
    .in('status', ['submitted', 'reviewed'])
    .maybeSingle();

  if (error) {
    console.error('감면 조회 에러:', error);
    return null;
  }
  return data;
}

// 내 감면 신청 목록 조회
export async function getMyDiscountApplications(): Promise<DiscountApplication[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  const { data, error } = await supabase
    .from('discount_applications')
    .select('*')
    .eq('user_id', user.id)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('내 감면 목록 조회 에러:', error);
    return [];
  }
  return data || [];
}

// =====================================================
// 관리자용 감면 서비스
// =====================================================

export async function adminGetDiscountApplications(
  statusFilter?: string
): Promise<DiscountWithApplication[]> {
  let query = supabase
    .from('discount_applications')
    .select('*')
    .order('created_at', { ascending: false });

  if (statusFilter && statusFilter !== 'all') {
    query = query.eq('status', statusFilter);
  }

  const { data, error } = await query;

  if (error) {
    console.error('감면 목록 조회 에러:', error);
    return [];
  }

  if (!data || data.length === 0) return [];

  // application 정보 조인
  const applicationIds = Array.from(new Set(data.map(d => d.application_id)));
  const userIds = Array.from(new Set(data.map(d => d.user_id)));

  const [applicationsRes, usersRes] = await Promise.all([
    selectInChunks<any>(
      applicationIds,
      slice => supabase
        .from('applications')
        .select('id, application_number, status, payment_status, payment_amount, class_id, student_name')
        .in('id', slice)
    ),
    selectInChunks<any>(
      userIds,
      slice => supabase
        .from('profiles')
        .select('id, name, phone, email, address, address_detail, birthdate, guardian_name, guardian_phone')
        .in('id', slice)
    ),
  ]);

  if (applicationsRes.error) console.error('감면-신청 조인 조회 에러:', applicationsRes.error);
  if (usersRes.error) console.error('감면-회원 조인 조회 에러:', usersRes.error);

  const appMap = new Map(
    (applicationsRes.data || []).map(a => [a.id, a])
  );
  const userMap = new Map(
    (usersRes.data || []).map(u => [u.id, u])
  );

  // class + program 정보 조인
  const classIds = Array.from(new Set(
    (applicationsRes.data || []).filter(a => a.class_id).map(a => a.class_id)
  ));

  let classMap = new Map();
  let programMap = new Map();

  if (classIds.length > 0) {
    const classRes = await selectInChunks<any>(
      classIds,
      slice => supabase
        .from('program_classes')
        .select('id, class_name, program_id')
        .in('id', slice)
    );

    classMap = new Map(
      (classRes.data || []).map(c => [c.id, c])
    );

    const programIds = Array.from(new Set(
      (classRes.data || []).filter(c => c.program_id).map(c => c.program_id)
    ));

    if (programIds.length > 0) {
      const programRes = await selectInChunks<any>(
        programIds,
        slice => supabase
          .from('programs')
          .select('id, name')
          .in('id', slice)
      );

      programMap = new Map(
        (programRes.data || []).map(p => [p.id, p])
      );
    }
  }

  return data.map(d => {
    const app = appMap.get(d.application_id);
    const userInfo = userMap.get(d.user_id);
    const classInfo = app ? classMap.get(app.class_id) : null;
    const programInfo = classInfo ? programMap.get(classInfo.program_id) : null;

    return {
      ...d,
      application: app ? {
        id: app.id,
        application_number: app.application_number,
        status: app.status,
        payment_status: app.payment_status,
        payment_amount: app.payment_amount,
        class_id: app.class_id,
      } : undefined,
      class_info: classInfo ? {
        id: classInfo.id,
        class_name: classInfo.class_name,
        program_id: classInfo.program_id,
      } : undefined,
      program_info: programInfo ? {
        id: programInfo.id,
        courseName: programInfo.name,
      } : undefined,
      user_info: userInfo ? {
        name: userInfo.name,
        phone: userInfo.phone,
        email: userInfo.email,
        address: userInfo.address,
        address_detail: userInfo.address_detail,
        birthdate: userInfo.birthdate,
        guardian_name: userInfo.guardian_name,
        guardian_phone: userInfo.guardian_phone,
      } : undefined,
    };
  });
}

// 관리자: 감면 확인 처리
export async function adminReviewDiscount(
  discountId: string,
  adminNote?: string
): Promise<{ success: boolean; error?: string }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { success: false, error: '로그인이 필요합니다.' };

  const { error } = await supabase
    .from('discount_applications')
    .update({
      status: 'reviewed',
      reviewed_at: new Date().toISOString(),
      reviewed_by: user.id,
      admin_note: adminNote || null,
      updated_at: new Date().toISOString(),
    })
    .eq('id', discountId);

  if (error) {
    return { success: false, error: error.message };
  }
  return { success: true };
}

// 관리자: 감면 거절 처리
export async function adminRejectDiscount(
  discountId: string,
  rejectionReason: string
): Promise<{ success: boolean; error?: string }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { success: false, error: '로그인이 필요합니다.' };

  const { error } = await supabase
    .from('discount_applications')
    .update({
      status: 'rejected',
      reviewed_at: new Date().toISOString(),
      reviewed_by: user.id,
      admin_note: rejectionReason,
      updated_at: new Date().toISOString(),
    })
    .eq('id', discountId);

  if (error) {
    return { success: false, error: error.message };
  }
  return { success: true };
}

// 상태 라벨/색상
export function getDiscountStatusLabel(status: string): string {
  switch (status) {
    case 'submitted': return '접수됨';
    case 'reviewed': return '확인완료';
    case 'rejected': return '거절';
    default: return status;
  }
}

export function getDiscountStatusColor(status: string): string {
  switch (status) {
    case 'submitted': return 'bg-amber-50 text-amber-600 border-amber-200';
    case 'reviewed': return 'bg-green-50 text-green-600 border-green-200';
    case 'rejected': return 'bg-red-50 text-red-600 border-red-200';
    default: return 'bg-gray-50 text-gray-600 border-gray-200';
  }
}
